import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useDeleteExpense } from "@/hooks/useExpenses";
import { useToast } from "@/hooks/use-toast";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";

import TabErrorBanner from "@/components/v2/molecules/admin/shared/TabErrorBanner";
import ExpenseFilters from "@/components/v2/molecules/admin/tabs/AdminExpensesTab/ExpenseFilters";
import ExpensesTabSkeleton from "@/components/v2/molecules/admin/tabs/AdminExpensesTab/ExpensesTabSkeleton";
import ReceiptPreviewDialog from "@/components/v2/molecules/admin/tabs/shared/ReceiptPreviewDialog";
import DeleteDialog from "@/components/v2/molecules/admin/tabs/shared/DeleteDialog";
import TablePagination from "@/components/v2/molecules/admin/shared/TablePagination";
import { useExpenseFilters } from "@/hooks/admin/tabs/useExpenseFilters";
import useRolePermissions from "@/hooks/admin/shared/useRolePermissions";
import useTransactionModals from "@/hooks/admin/shared/useTransactionModals";
import type { Expense } from "@/hooks/useExpenses";
import { Plus, Trash2, Download, Pencil } from "lucide-react";
import ExpenseFormModal from "./ExpenseFormModal";
import BulkEditExpenseModal from "./BulkEditExpenseModal";
import ExpensesTable from "./ExpensesTable";
import { useShiftSelect } from "@/hooks/useShiftSelect";
import AdminPageHeader from '@/components/v2/atoms/admin/layout/AdminPageHeader';

const AdminExpensesTab = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const { canCreate, canDelete, canEdit, role } = useRolePermissions();
  const { toast } = useToast();

  const deleteExpenseMutation = useDeleteExpense();

  const {
    itemToEdit: expenseToEdit,
    isModalOpen: isExpenseModalOpen,
    isDeleteDialogOpen: isDeleteExpenseDialogOpen,
    setIsDeleteDialogOpen: setIsDeleteExpenseDialogOpen,
    isReceiptModalOpen,
    receiptUrlToPreview,
    handleNew: handleNewExpense,
    handleEdit: handleEditExpense,
    handleModalClose: handleExpenseModalClose,
    handleDelete: handleDeleteExpense,
    confirmDelete: confirmDeleteExpense,
    handleViewReceipt,
    handleReceiptModalClose,
  } = useTransactionModals<Expense>({
    onDelete: async (id) => deleteExpenseMutation.mutateAsync(id),
    withReceipt: true,
  });

  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  const {
    filteredExpenses,
    categories,
    properties,
    tags,
    isLoading: isLoadingExpenses,
    error: errorExpenses,
    refetch: refetchExpenses,
    // Pagination
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    goToPage,
    changePageSize,
    // Filters
    searchTerm,
    selectedCategory,
    selectedTag,
    selectedOrganizations,
    selectedProperties,
    dateFrom,
    dateTo,
    hasActiveFilters,
    setSearchTerm,
    setSelectedCategory,
    setSelectedTag,
    setSelectedOrganizations,
    setSelectedProperties,
    setDateFrom,
    setDateTo,
    clearFilters,
  } = useExpenseFilters();

  // Shift-select hook
  const {
    selectedItems: selectedExpenses,
    setSelectedItems: setSelectedExpenses,
    handleSelect: handleSelectExpense,
    handleSelectAll,
    clearSelection,
  } = useShiftSelect({ items: filteredExpenses });

  const handleEditSelected = () => {
    setIsBulkEditModalOpen(true);
  };

  // Calculate total amount for selected expenses
  const selectedExpensesTotal = useMemo(() => {
    if (selectedExpenses.length === 0) return 0;
    
    return filteredExpenses
      .filter(expense => selectedExpenses.includes(expense.id))
      .reduce((sum, expense) => sum + (expense.amount || 0), 0);
  }, [selectedExpenses, filteredExpenses]);

  const handleDeleteSelected = () => {
    setIsBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(
        selectedExpenses.map(expenseId => 
          deleteExpenseMutation.mutateAsync(expenseId)
        )
      );

      toast({
        title: "Éxito",
        description: `Se eliminaron ${selectedExpenses.length} gasto(s) correctamente`,
      });

      clearSelection();
      setIsBulkDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting selected expenses:", error);
      toast({
        title: "Error",
        description: "Error al eliminar los gastos seleccionados",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadCSV = () => {
    // Si hay gastos seleccionados, exportar solo esos; si no, exportar todos los filtrados
    const expensesToExport = selectedExpenses.length > 0
      ? filteredExpenses.filter(expense => selectedExpenses.includes(expense.id))
      : filteredExpenses;

    const csvHeaders = [
      'Fecha',
      'Propiedad',
      'Categoría',
      'Subcategoría',
      'Descripción',
      'Monto',
      'Estatus',
      'Forma de Pago',
    ];

    const csvData = expensesToExport.map((expense) => [
      expense.expense_date,
      expense.properties?.short_name || 'N/A',
      expense.category?.name || 'N/A',
      expense.subcategory?.name || 'N/A',
      expense.description || 'N/A',
      expense.amount.toString(),
      expense.status,
      expense.payment_method,
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `gastos_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'CSV descargado',
      description: `Se han exportado ${expensesToExport.length} gastos`,
    });
  };

  if (isLoadingExpenses) {
    return <ExpensesTabSkeleton />;
  }

  if (errorExpenses) {
    return (
      <TabErrorBanner
        title="Error al cargar gastos"
        message="No se pudieron cargar los gastos. Por favor, intenta nuevamente."
        onRetry={() => refetchExpenses()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <AdminPageHeader title="Gastos">
          <div className="flex items-center gap-3">
            {selectedExpenses.length > 0 && (
              <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                {selectedExpenses.length} seleccionado{selectedExpenses.length > 1 ? 's' : ''} • ${selectedExpensesTotal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            )}

            <div className="flex items-center gap-2">
              {selectedExpenses.length > 0 && (
                <>
                  {canEdit && (
                    <Button
                      variant="outline"
                      onClick={handleEditSelected}
                      className="flex items-center gap-2"
                    >
                      <Pencil size={16} />
                      Editar ({selectedExpenses.length})
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="destructive"
                      onClick={handleDeleteSelected}
                      className="flex items-center gap-2"
                      disabled={isDeleting}
                    >
                      <Trash2 size={16} />
                      {isDeleting ? "Eliminando..." : `Eliminar (${selectedExpenses.length})`}
                    </Button>
                  )}
                </>
              )}

              <Button
                onClick={handleDownloadCSV}
                variant="outline"
                disabled={filteredExpenses.length === 0}
                className="flex items-center gap-2"
              >
                <Download size={16} />
                Descargar CSV
              </Button>

              {canCreate && filteredExpenses.length !== 0 && (
                <Button
                  onClick={handleNewExpense}
                  className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
                >
                  <Plus size={16} />
                  Crear Gasto
                </Button>
              )}
            </div>
          </div>
        </AdminPageHeader>

        <ExpenseFilters
          categories={categories}
          tags={tags}
          searchTerm={searchTerm}
          dateFrom={dateFrom}
          dateTo={dateTo}
          hasActiveFilters={hasActiveFilters}
          properties={properties}
          organizations={organizations}
          contracts={contracts}
          selectedCategory={selectedCategory}
          selectedTag={selectedTag}
          selectedOrganizations={selectedOrganizations}
          selectedProperties={selectedProperties}
          clearFilters={clearFilters}
          setSearchTerm={setSearchTerm}
          setDateFrom={setDateFrom}
          setDateTo={setDateTo}
          setSelectedCategory={setSelectedCategory}
          setSelectedTag={setSelectedTag}
          setSelectedOrganizations={setSelectedOrganizations}
          setSelectedProperties={setSelectedProperties}
        />
      </div>

      {filteredExpenses.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center flex flex-col items-center">
          <h3 className="text-gray-800 font-medium mb-2">No hay gastos</h3>

          <p className="text-gray-600 text-sm mb-4">
            No se encontraron gastos que coincidan con los filtros aplicados.
          </p>

          {canCreate && (
            <Button
              onClick={handleNewExpense}
              className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear Gasto
            </Button>
          )}
        </div>
      ) : (
        <>
          <ExpensesTable
            expenses={filteredExpenses}
            onViewReceipt={handleViewReceipt}
            onEdit={handleEditExpense}
            onDelete={handleDeleteExpense}
            onSelectExpense={handleSelectExpense}
            selectedExpenses={selectedExpenses}
            onSelectAll={handleSelectAll}
            canEdit={canEdit}
            canDelete={canDelete}
          />

          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={goToPage}
            onPageSizeChange={changePageSize}
            pageSizeOptions={[100, 500]}
          />
        </>
      )}

      <ExpenseFormModal
        isOpen={isExpenseModalOpen}
        onClose={handleExpenseModalClose}
        expense={expenseToEdit}
        defaultPropertyId={selectedProperties[0]}
      />

      <BulkEditExpenseModal
        isOpen={isBulkEditModalOpen}
        onClose={() => {
          setIsBulkEditModalOpen(false);
          clearSelection();
        }}
        selectedExpenseIds={selectedExpenses}
      />

      <ReceiptPreviewDialog
        title="Comprobante de Gasto"
        isOpen={isReceiptModalOpen}
        onClose={handleReceiptModalClose}
        receiptUrl={receiptUrlToPreview}
      />

      <DeleteDialog
        title="¿Estás seguro?"
        description=" Esta acción no se puede deshacer. Se eliminará permanentemente el registro de gasto."
        open={isDeleteExpenseDialogOpen}
        onOpenChange={setIsDeleteExpenseDialogOpen}
        onConfirm={confirmDeleteExpense}
      />

      <DeleteDialog
        title={`¿Eliminar ${selectedExpenses.length} gasto(s)?`}
        description={`Esta acción no se puede deshacer. Se eliminarán permanentemente ${selectedExpenses.length} registro(s) de gasto.`}
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        onConfirm={confirmBulkDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AdminExpensesTab;
